/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Button, Box, Paper, Typography, Grid } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ReactHookFormTextField } from '@opengeoweb/form-fields';
import { useConfirmationDialog } from '@opengeoweb/shared';
import { useWarningsTranslation } from '../../../utils/i18n';
import { isWarningFinalised } from '../WarningEventForm/utils';

export const SUMMARY_HEADLINE_LIMIT = 150;
export const SUMMARY_CONCISE_SUMMARY_LIMIT = 1500;
export const SUMMARY_DESCRIPTION_LIMIT = 2500;
export const SUMMARY_FIELDS_MIN_LENGTH = 20;

const SUMMARY_TEXT_FIELDS = [
  'headlineOriginal',
  'headlineTranslation',
  'conciseSummaryOriginal',
  'conciseSummaryTranslation',
  'descriptionOriginal',
  'descriptionTranslation',
] as const;

export const RemainingChars: React.FC<{
  name: string;
  limit: number;
  t: TFunction<'warn'>;
}> = ({ name, limit, t }) => {
  const curValue = useWatch({ name }) ?? '';
  const remaining = Math.max(-1, limit - curValue.length);
  return (
    <Typography
      component="div"
      sx={{
        marginBottom: '2px',
        marginLeft: 1,
        marginTop: '2px',
      }}
      variant="caption"
    >
      <Typography
        color={remaining === -1 ? 'error' : 'textSecondary'}
        component="span"
        variant="inherit"
      >
        {curValue.length}
      </Typography>
      <Typography color="textSecondary" component="span" variant="inherit">
        {`/${limit} ${t('warning-event-summary-max-chars')}`}
      </Typography>
    </Typography>
  );
};

interface FormProps {
  isReadOnly?: boolean;
  onClickAdd?: () => void;
}

export const WarningEventSummaryForm: React.FC<FormProps> = ({
  isReadOnly = false,
  onClickAdd = (): void => {},
}) => {
  const { t } = useWarningsTranslation();
  const { setValue, control } = useFormContext();
  const showConfirmDialog = useConfirmationDialog();

  const texts = useWatch({ control, name: 'texts' });
  const warnings = useWatch({ control, name: 'warnings' }) ?? [];
  // When the whole event is expired/withdrawn the summary can no longer be edited
  const isEventFinalised =
    warnings.length > 0 && warnings.every(isWarningFinalised);
  const areFieldsDisabled = isReadOnly || isEventFinalised;

  const hasContent = SUMMARY_TEXT_FIELDS.some((field) =>
    (texts?.[field] ?? '').trim(),
  );

  const onClear = React.useCallback((): void => {
    SUMMARY_TEXT_FIELDS.forEach((field) => {
      setValue(`texts.${field}`, '');
    });
  }, [setValue]);

  const onClearWithConfirmation = React.useCallback(async (): Promise<void> => {
    if (!hasContent) {
      onClear();
      return;
    }
    await showConfirmDialog({
      title: t('warning-event-summary-dialog-clear-confirm-title'),
      description: t('warning-event-summary-dialog-clear-confirm-description'),
      confirmLabel: t('warning-event-summary-dialog-clear-button'),
      cancelLabel: t('close-dialog-cancel'),
    });
    onClear();
  }, [hasContent, onClear, showConfirmDialog, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'geowebColors.background.surfaceApp',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: 0,
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography
                color="textSecondary"
                sx={{
                  fontSize: '12px',
                  paddingY: '8px',
                  paddingLeft: '8px',
                }}
                variant="caption"
              >
                {t('warning-event-summary-language-original')}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Paper
                sx={{
                  padding: 1,
                  backgroundColor: 'geowebColors.background.surface',
                  width: '100%',
                  boxShadow: 'none',
                }}
              >
                <Grid
                  columnSpacing={2}
                  container
                  rowSpacing={1}
                  sx={{ marginTop: 0.5 }}
                >
                  <Grid size={{ xs: 12 }}>
                    <ReactHookFormTextField
                      disabled={areFieldsDisabled}
                      label={t('warning-event-summary-headline-original')}
                      maxRows={6}
                      minRows={4}
                      multiline
                      name="texts.headlineOriginal"
                      // Validation rules have to be set in WarningEventFormContent!
                    />
                    <RemainingChars
                      limit={SUMMARY_HEADLINE_LIMIT}
                      name="texts.headlineOriginal"
                      t={t}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ReactHookFormTextField
                      disabled={areFieldsDisabled}
                      label={t(
                        'warning-event-summary-concise-summary-original',
                      )}
                      maxRows={9}
                      minRows={7}
                      multiline
                      name="texts.conciseSummaryOriginal"
                      // Validation rules have to be set in WarningEventFormContent!
                    />
                    <RemainingChars
                      limit={SUMMARY_CONCISE_SUMMARY_LIMIT}
                      name="texts.conciseSummaryOriginal"
                      t={t}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ReactHookFormTextField
                      disabled={areFieldsDisabled}
                      label={t('warning-event-summary-description-original')}
                      maxRows={11}
                      minRows={9}
                      multiline
                      name="texts.descriptionOriginal"
                      // Validation rules have to be set in WarningEventFormContent!
                    />
                    <RemainingChars
                      limit={SUMMARY_DESCRIPTION_LIMIT}
                      name="texts.descriptionOriginal"
                      t={t}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: 0,
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography
                color="textSecondary"
                sx={{
                  fontSize: '12px',
                  paddingY: '8px',
                  paddingLeft: '8px',
                }}
                variant="caption"
              >
                {t('warning-event-summary-language-translation')}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Paper
                sx={{
                  padding: 1,
                  backgroundColor: 'geowebColors.background.surface',
                  width: '100%',
                  boxShadow: 'none',
                }}
              >
                <Grid
                  columnSpacing={2}
                  container
                  rowSpacing={1}
                  sx={{ marginTop: 0.5 }}
                >
                  <Grid size={{ xs: 12 }}>
                    <ReactHookFormTextField
                      disabled={areFieldsDisabled}
                      label={t('warning-event-summary-headline-translation')}
                      maxRows={6}
                      minRows={4}
                      multiline
                      name="texts.headlineTranslation"
                      // Validation rules have to be set in WarningEventFormContent!
                    />
                    <RemainingChars
                      limit={SUMMARY_HEADLINE_LIMIT}
                      name="texts.headlineTranslation"
                      t={t}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ReactHookFormTextField
                      disabled={areFieldsDisabled}
                      label={t(
                        'warning-event-summary-concise-summary-translation',
                      )}
                      maxRows={9}
                      minRows={7}
                      multiline
                      name="texts.conciseSummaryTranslation"
                      // Validation rules have to be set in WarningEventFormContent!
                    />
                    <RemainingChars
                      limit={SUMMARY_CONCISE_SUMMARY_LIMIT}
                      name="texts.conciseSummaryTranslation"
                      t={t}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ReactHookFormTextField
                      disabled={areFieldsDisabled}
                      label={t('warning-event-summary-description-translation')}
                      maxRows={11}
                      minRows={9}
                      multiline
                      name="texts.descriptionTranslation"
                      // Validation rules have to be set in WarningEventFormContent!
                    />
                    <RemainingChars
                      limit={SUMMARY_DESCRIPTION_LIMIT}
                      name="texts.descriptionTranslation"
                      t={t}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>
      {!areFieldsDisabled && (
        <Box
          sx={{
            flex: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'geowebColors.background.surfaceApp',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              '& button:not(:last-child)': { mr: 3 },
            }}
          >
            <Button
              onClick={onClearWithConfirmation}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              title={t('warning-event-summary-dialog-clear-button-title')}
              variant="tertiary"
            >
              {t('warning-event-summary-dialog-clear-button')}
            </Button>
            <Button
              onClick={onClickAdd}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              title={t('warning-event-summary-dialog-OK-button-title')}
              variant="primary"
            >
              {t('warning-event-summary-dialog-OK-button')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
